import React, { ReactElement } from 'react'

import styled from '@emotion/styled'

import { useHelpTextId } from '../../../hooks/useHelpTextId'
import { ThemeSpacing } from '../../../theme/spacing'
import { Flex, FlexGrow } from '../../layout/Flex'
import BaseCell, { BaseCellProps } from '../BaseCell/BaseCell'
import { cellHoverStyles } from '../InteractiveCell/InteractiveCell'
import ArcadeRadioButton, { ArcadeRadioButtonProps } from '../RadioButton/ArcadeRadioButton'

const StyledLabel = styled.label<{ disabled: boolean }>`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: block;
  position: relative;
  margin: -${props => props.theme.spacing.m} -${props => props.theme.spacing.l};
  padding: ${props => props.theme.spacing.m} ${props => props.theme.spacing.l};

  ${({ theme, disabled }) => cellHoverStyles(theme, disabled)}

  /* focused */
  ${({ theme }) => theme.selectors.keyboard} &:has(input:focus) {
    border-radius: 0.25rem;
    outline: 0.125rem solid ${({ theme }) => theme.arcadeColors.component.inputBorderFocus};
  }
`
const Accessory = styled(Flex)<{ disabled: boolean }>`
  opacity: ${({ disabled }) => (disabled ? 0.4 : undefined)};
`
export interface RadioCellProps extends ArcadeRadioButtonProps, Pick<BaseCellProps, 'label'> {
  cellProps?: Omit<BaseCellProps, 'accessory' | 'disabled' | 'label'>
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
  headerSpacing?: ThemeSpacing[keyof ThemeSpacing]
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  accessoryLabel?: ReactElement
}

const RadioCell = React.forwardRef<HTMLDivElement, RadioCellProps>((props, ref) => {
  const { label, disabled = false, checked, cellProps, labelProps, headerSpacing, accessoryLabel, ...restProps } = props

  const helpTextId = useHelpTextId(cellProps)

  return (
    <FlexGrow>
      <StyledLabel disabled={disabled} {...labelProps}>
        <BaseCell
          label={label}
          ref={ref}
          accessory={
            <Accessory flexDirection="row" gap="s" disabled={disabled}>
              {accessoryLabel}
              <ArcadeRadioButton
                checked={checked}
                disabled={disabled}
                data-testid="visible-radio"
                helpTextId={helpTextId}
                {...restProps}
              />
            </Accessory>
          }
          disabled={disabled}
          headerSpacing={headerSpacing}
          {...cellProps}
          helpTextId={helpTextId}
        />
      </StyledLabel>
    </FlexGrow>
  )
})

export default RadioCell
